import React, { useEffect, useState } from "react";
import { BASE_CRM_URL } from "../../../../../components/config/config";
import Swal from "sweetalert2";
import { Drawer, Empty, Spin } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

const Notes = ({ recordId, type, open, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [addNoteLoading, setAddNoteLoading] = useState(false);
  const [updateNoteLoading, setUpdateNoteLoading] = useState(false);

  const [noteData, setNoteData] = useState({
    description: "",
    date_contacted: null,
  });

  const [editingNoteId, setEditingNoteId] = useState(null);

  const [editNoteData, setEditNoteData] = useState({
    description: "",
  });

  const relTypeMap = {
    proposal: "Proposal",
    estimate: "Estimate",
    invoice: "Invoice",
    creditnote: "CreditNote",
  };

  const relType = relTypeMap[type];

  // --------------------------------------------------
  // Fetch Notes
  // --------------------------------------------------
  const fetchNotes = async () => {
    if (!recordId || !relType) {
      setNotes([]);
      return;
    }

    try {
      setNotesLoading(true);

      const response = await fetch(
        `${BASE_CRM_URL}notes/GetNotes?rel_id=${recordId}&rel_type=${relType}`,
      );

      const data = await response.json();

      if (data.Status) {
        setNotes(data.ResultData || []);
      } else {
        setNotes([]);
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  // --------------------------------------------------
  // Edit Note
  // --------------------------------------------------
  const handleEditNote = (note) => {
    setEditingNoteId(note.id);

    setEditNoteData({
      description: note.description || "",
    });
  };

  // --------------------------------------------------
  // Cancel Edit
  // --------------------------------------------------
  const handleCancelEdit = () => {
    if (updateNoteLoading) return;

    setEditingNoteId(null);

    setEditNoteData({
      description: "",
    });
  };

  // --------------------------------------------------
  // Update Note
  // --------------------------------------------------
  const handleUpdateNote = async () => {
    if (!editNoteData.description.trim()) {
      Swal.fire({
        icon: "error",
        title: "Please enter a note.",
      });
      return;
    }

    try {
      setUpdateNoteLoading(true);

      const response = await fetch(`${BASE_CRM_URL}notes/UpdateNote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: editingNoteId,
          description: editNoteData.description.trim(),
        }),
      });

      const data = await response.json();

      if (data.Status) {
        setEditingNoteId(null);

        setEditNoteData({
          description: "",
        });

        setNoteData({
          description: "",
          date_contacted: null,
        });

        await fetchNotes();

        Swal.fire({
          icon: "success",
          title: "Success",
          text: data.Message,
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data.Message,
        });
      }
    } catch (error) {
      console.error("Error updating note:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong while updating the note.",
      });
    } finally {
      setUpdateNoteLoading(false);
    }
  };

  // --------------------------------------------------
  // Add Note
  // --------------------------------------------------
  const handleAddNote = async () => {
    if (!noteData.description.trim()) {
      Swal.fire({
        icon: "error",
        title: "Please enter a note.",
      });
      return;
    }

    try {
      setAddNoteLoading(true);

      const userDetails = JSON.parse(sessionStorage.getItem("userDetails"));

      const userId = userDetails?.staffid;

      if (!recordId || !relType) {
        console.error("Invalid recordId or type");
        return;
      }

      const response = await fetch(`${BASE_CRM_URL}notes/AddNote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rel_id: recordId,
          rel_type: relType,
          description: noteData.description.trim(),
          addedfrom: userId,
        }),
      });

      const data = await response.json();

      if (data.Status) {
        setNoteData({
          description: "",
          date_contacted: null,
        });

        await fetchNotes();

        Swal.fire({
          icon: "success",
          title: "Success",
          text: data.Message,
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data.Message,
        });
      }
    } catch (error) {
      console.error("Error adding note:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong while adding the note.",
      });
    } finally {
      setAddNoteLoading(false);
    }
  };

  // --------------------------------------------------
  // Load Notes when Drawer opens / record changes
  // --------------------------------------------------
  useEffect(() => {
    if (open) {
      fetchNotes();
    }
  }, [open, recordId, type]);

  const globalLoading = addNoteLoading || updateNoteLoading;

  return (
    <Drawer
      title="Notes"
      open={open}
      onClose={globalLoading ? undefined : onClose}
      width={550}
      maskClosable={!globalLoading}
      destroyOnClose
      closable={false}
      extra={
        <div className="d-flex justify-content-end gap-2">
          {/* Close Button */}
          <button
            type="button"
            onClick={onClose}
            disabled={globalLoading}
            className="btn rounded-circle d-flex align-items-center justify-content-center shadow-sm"
            style={{
              width: "35px",
              height: "35px",
              backgroundColor: "#e40d22",
              color: "#fff",
              border: "none",
            }}
            title="Close"
          >
            <i className="fa fa-times" />
          </button>

          {/* Add Button */}
          {editingNoteId === null && (
            <button
              type="button"
              onClick={handleAddNote}
              disabled={globalLoading}
              className="btn rounded-circle d-flex align-items-center justify-content-center shadow-sm"
              style={{
                width: "35px",
                height: "35px",
                backgroundColor: "#07f083",
                color: "#fff",
                border: "none",
              }}
              title="Add Note"
            >
              <i className="fa fa-check" />
            </button>
          )}

          {/* Update Button */}
          {editingNoteId !== null && (
            <button
              type="button"
              onClick={handleUpdateNote}
              disabled={globalLoading}
              className="btn rounded-circle d-flex align-items-center justify-content-center shadow-sm"
              style={{
                width: "35px",
                height: "35px",
                backgroundColor: "#07f083",
                color: "#fff",
                border: "none",
              }}
              title="Update Note"
            >
              <i className="fa fa-check" />
            </button>
          )}
        </div>
      }
    >
      {/* Global Loading Overlay */}
      {globalLoading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 9999,
            backgroundColor: "rgba(255, 255, 255, 0.65)",
          }}
        >
          <Spin size="large" />
        </div>
      )}

      <div className="container">
        <div className="row">
          {/* Notes Loading */}
          {notesLoading ? (
            <div className="col-12 text-center py-4">
              <Spin />
            </div>
          ) : (
            <>
              {/* Add Note Form */}
              {editingNoteId === null && (
                <div className="col-12 mb-3">
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Enter your note here..."
                    value={noteData.description}
                    disabled={globalLoading}
                    onChange={(e) =>
                      setNoteData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>
              )}

              {/* Notes List */}
              {notes.length > 0 ? (
                <div className="col-12 mt-2">
                  {notes.map((note) => (
                    <div key={note.id} className="card border-0 shadow mb-3">
                      <div className="card-body">
                        {editingNoteId === note.id ? (
                          <>
                            <textarea
                              rows={4}
                              className="form-control mb-3"
                              value={editNoteData.description}
                              disabled={globalLoading}
                              onChange={(e) =>
                                setEditNoteData({
                                  description: e.target.value,
                                })
                              }
                            />

                            <div className="d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn btn-light"
                                onClick={handleCancelEdit}
                                disabled={globalLoading}
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="d-flex justify-content-between align-items-start">
                              <p
                                className="mb-2"
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {note.description}
                              </p>

                              {/* Edit Button */}
                              <button
                                type="button"
                                className="btn rounded-circle d-flex align-items-center justify-content-center shadow-sm ms-2"
                                onClick={() => handleEditNote(note)}
                                disabled={globalLoading}
                                style={{
                                  width: "35px",
                                  height: "35px",
                                  minWidth: "35px",
                                  backgroundColor: "#1B2850",
                                  color: "#fff",
                                  border: "none",
                                }}
                                title="Edit"
                              >
                                <i className="fa-solid fa-pen-to-square" />
                              </button>
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <small className="text-muted me-3">
                                  <i className="fa-regular fa-user me-2" />
                                  {note.added_by_name}
                                </small>

                                <small className="text-muted">
                                  <i className="fa-regular fa-calendar me-2" />
                                  {dayjs(note.dateadded).format(
                                    "DD-MM-YYYY hh:mm A",
                                  )}
                                </small>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="col-12">
                  <Empty description="No notes found" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

Notes.propTypes = {
  recordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default Notes;
